import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Percent as PercentIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal";
import DeliveryCenterCostsService from "../../../api/DeliveryCenter/deliveryCenterCosts";
import MoneyTextField from "../../Inputs/MoneyTextField";

/**
 * Modal para criar ou editar registros de custo
 * @param {Object} props Propriedades do componente
 * @returns {JSX.Element} Componente do modal de custo
 */
const CostModal = ({
  isOpen,
  onClose,
  editMode,
  initialData,
  categories,
  suppliers,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    category: "",
    supplier: "",
    value: "",
    date: dayjs(),
    invoice_number: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [useMultipleCategories, setUseMultipleCategories] = useState(false);
  const [categoryAllocations, setCategoryAllocations] = useState([]);
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [inputMode, setInputMode] = useState("amount");

  // Reset useMultipleCategories whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setUseMultipleCategories(false);
    }
  }, [isOpen]);

  // Carrega dados quando estiver editando um custo existente
  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        ...initialData,
        date: dayjs(initialData.date),
      });
      return;
    }

    if (isOpen && !editMode) {
      setFormData({
        category: "",
        supplier: "",
        value: "",
        date: dayjs(),
        invoice_number: "",
        notes: "",
      });
      setCategoryAllocations([]);
      setTotalPercentage(0);
      setInputMode("amount");
    }
  }, [initialData, isOpen, editMode]);

  // Converte valor formatado com vírgula (R$ 1.000,00) para número (1000.00)
  const parseMoneyValue = (value) => {
    if (!value) {
      return 0;
    }

    const numericValue = value
      .replace(/^R\$\s*/i, "")
      .replace(/\./g, "")
      .replace(/,/g, ".")
      .trim();

    return parseFloat(numericValue) || 0;
  };

  // Inicializa todas as categorias disponíveis com distribuição uniforme
  const initializeAllCategories = () => {
    if (categories.length === 0) {
      return;
    }

    const totalValue = parseMoneyValue(formData.value);
    const evenPercentage = 100 / categories.length;

    const allocations = categories.map((category) => {
      const amount =
        totalValue > 0
          ? ((totalValue * evenPercentage) / 100).toFixed(2).replace(".", ",")
          : "0,00";

      return {
        id: Date.now() + category.id,
        categoryId: category.id,
        percentage: parseFloat(evenPercentage.toFixed(2)),
        amount: amount,
      };
    });

    setCategoryAllocations(allocations);
    updateTotalPercentage(allocations);
  };

  // Manipula alterações nos campos do formulário
  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }

    if (name === "value" && useMultipleCategories) {
      if (inputMode === "amount") {
        recalculatePercentagesFromAmounts();
        return;
      }

      recalculateAmountsFromPercentages();
    }
  };

  // Alterna o modo de múltiplas categorias
  const handleMultipleCategoriesToggle = (event) => {
    const checked = event.target.checked;
    setUseMultipleCategories(checked);

    if (checked) {
      initializeAllCategories();
      return;
    }

    setFormData((prev) => ({
      ...prev,
      category:
        categoryAllocations.length > 0 ? categoryAllocations[0].categoryId : "",
    }));
  };

  // Alterna entre modos de entrada (porcentagem ou valor direto)
  const handleInputModeChange = (event, newMode) => {
    if (newMode === null) {
      return;
    }

    setInputMode(newMode);

    if (newMode === "percentage") {
      recalculatePercentagesFromAmounts();
      return;
    }

    if (newMode === "amount") {
      recalculateAmountsFromPercentages();
    }
  };

  // Recalcula porcentagens com base nos valores diretos
  const recalculatePercentagesFromAmounts = (
    allocations = categoryAllocations
  ) => {
    const totalValue = parseMoneyValue(formData.value);
    if (totalValue <= 0) {
      // When removing an item, we might need to clear percentages even if total value is 0
      const resetAllocations = allocations.map((alloc) => ({
        ...alloc,
        percentage: 0,
      }));
      setCategoryAllocations(resetAllocations);
      updateTotalPercentage(resetAllocations);
      return;
    }

    const updatedAllocations = allocations.map((allocation) => {
      const amount = parseMoneyValue(allocation.amount) || 0;
      const percentage = totalValue > 0 ? (amount / totalValue) * 100 : 0;
      return {
        ...allocation,
        percentage: parseFloat(percentage.toFixed(2)),
      };
    });

    setCategoryAllocations(updatedAllocations);
    updateTotalPercentage(updatedAllocations);
  };

  // Recalcula valores diretos com base nas porcentagens
  const recalculateAmountsFromPercentages = () => {
    const totalValue = parseMoneyValue(formData.value);

    const updatedAllocations = categoryAllocations.map((allocation) => {
      const percentage = parseFloat(allocation.percentage) || 0;
      const amount = (totalValue * percentage) / 100;
      return {
        ...allocation,
        amount: amount.toFixed(2).replace(".", ","),
      };
    });

    setCategoryAllocations(updatedAllocations);
  };

  // Adiciona uma nova alocação de categoria
  const handleAddCategoryAllocation = () => {
    const usedCategoryIds = categoryAllocations.map((a) => a.categoryId);
    const availableCategory = categories.find(
      (c) => !usedCategoryIds.includes(c.id)
    );

    let newAllocation = {
      id: Date.now(),
      categoryId: availableCategory ? availableCategory.id : "",
    };

    if (inputMode === "percentage") {
      const remainingPercentage = 100 - totalPercentage;
      newAllocation.percentage =
        remainingPercentage > 0 ? remainingPercentage : 0;

      const totalValue = parseMoneyValue(formData.value);
      const amount = (totalValue * newAllocation.percentage) / 100;
      newAllocation.amount = amount.toFixed(2).replace(".", ",");
    } else {
      newAllocation.amount = "0,00";
      newAllocation.percentage = 0;
    }

    setCategoryAllocations((prev) => [...prev, newAllocation]);
    updateTotalPercentage([...categoryAllocations, newAllocation]);
  };

  // Remove uma alocação de categoria
  const handleRemoveCategoryAllocation = (id) => {
    const updatedAllocations = categoryAllocations.filter(
      (allocation) => allocation.id !== id
    );

    // The state update is scheduled here, but the functions below
    // will now use the correct `updatedAllocations` array immediately.
    setCategoryAllocations(updatedAllocations);
    updateTotalPercentage(updatedAllocations);

    if (inputMode === "amount") {
      // Pass the new array directly to the function
      recalculatePercentagesFromAmounts(updatedAllocations);
    }
  };
  // Atualiza o ID da categoria para uma alocação
  const handleCategoryChange = (id, categoryId) => {
    const updatedAllocations = categoryAllocations.map((allocation) =>
      allocation.id === id ? { ...allocation, categoryId } : allocation
    );
    setCategoryAllocations(updatedAllocations);
  };

  // Atualiza a porcentagem para uma alocação
  const handlePercentageChange = (id, value) => {
    const percentage = parseFloat(value) || 0;
    const updatedAllocations = categoryAllocations.map((allocation) => {
      if (allocation.id === id) {
        const updatedAllocation = { ...allocation, percentage };

        const totalValue = parseMoneyValue(formData.value);
        const amount = (totalValue * percentage) / 100;
        updatedAllocation.amount = amount.toFixed(2).replace(".", ",");

        return updatedAllocation;
      }
      return allocation;
    });

    setCategoryAllocations(updatedAllocations);
    updateTotalPercentage(updatedAllocations);
  };

  // Atualiza o valor direto para uma alocação
  const handleAmountChange = (id, value) => {
    const amount = value;
    const amountValue = parseMoneyValue(amount);
    const totalValue = parseMoneyValue(formData.value);

    const updatedAllocations = categoryAllocations.map((allocation) => {
      if (allocation.id === id) {
        const updatedAllocation = { ...allocation, amount };

        const percentage =
          totalValue > 0 ? (amountValue / totalValue) * 100 : 0;
        updatedAllocation.percentage = parseFloat(percentage.toFixed(2));

        return updatedAllocation;
      }
      return allocation;
    });

    setCategoryAllocations(updatedAllocations);
    updateTotalPercentage(updatedAllocations);
  };

  // Calcula a porcentagem total e valida
  const updateTotalPercentage = (allocations) => {
    const total = allocations.reduce(
      (sum, allocation) => sum + (parseFloat(allocation.percentage) || 0),
      0
    );
    setTotalPercentage(parseFloat(total.toFixed(2)));

    if (total > 100) {
      setErrors((prev) => ({
        ...prev,
        percentageTotal: "A porcentagem total não pode exceder 100%",
      }));
    } else if (total < 100 && allocations.length > 0) {
      setErrors((prev) => ({
        ...prev,
        percentageTotal: "A porcentagem total deve ser igual a 100%",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        percentageTotal: undefined,
      }));
    }
  };

  // Calcula o valor para cada categoria com base na porcentagem
  const calculateAmount = (percentage) => {
    const totalValue = parseMoneyValue(formData.value);
    const amount = (totalValue * percentage) / 100;
    return amount.toFixed(2).replace(".", ",");
  };

  // Validação do formulário
  const validateForm = () => {
    const newErrors = {};

    if (useMultipleCategories) {
      if (categoryAllocations.length === 0) {
        newErrors.categories = "Pelo menos uma categoria é necessária";
      }

      if (totalPercentage !== 100) {
        newErrors.percentageTotal = "A porcentagem total deve ser igual a 100%";
      }

      const hasEmptyCategory = categoryAllocations.some(
        (item) => !item.categoryId
      );
      if (hasEmptyCategory) {
        newErrors.categories = "Selecione uma categoria para cada alocação";
      }
    } else {
      if (!formData.category) {
        newErrors.category = "Categoria é obrigatória";
      }
    }

    if (!formData.value) {
      newErrors.value = "Valor é obrigatório";
    }

    if (!formData.date) {
      newErrors.date = "Data é obrigatória";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Envio do formulário
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const dateFormatted = dayjs(formData.date).format("YYYY-MM-DD");
      const totalValue = parseMoneyValue(formData.value);

      if (useMultipleCategories) {
        const costBatchData = categoryAllocations.map((allocation) => ({
          category: allocation.categoryId,
          supplier: formData.supplier,
          value: Math.round(
            (inputMode === "percentage"
              ? (totalValue * allocation.percentage) / 100
              : parseMoneyValue(allocation.amount)) * 100
          ), // Convert to cents
          date: dateFormatted,
          invoice_number: formData.invoice_number,
          notes: formData.notes,
        }));

        await DeliveryCenterCostsService.createCostBatch(costBatchData);
        onSuccess("Múltiplos custos criados com sucesso!");
      } else if (editMode) {
        const costData = {
          ...formData,
          value: Math.round(totalValue * 100), // Convert to cents
          date: dateFormatted,
        };
        await DeliveryCenterCostsService.updateCost(formData.id, costData);
        onSuccess("Custo atualizado com sucesso!");
      } else {
        const costData = {
          ...formData,
          value: Math.round(totalValue * 100), // Convert to cents
          date: dateFormatted,
        };
        await DeliveryCenterCostsService.createCost(costData);
        onSuccess("Custo criado com sucesso!");
      }

      onClose();
    } catch (err) {
      console.error("Erro ao salvar custo:", err);
      setErrors({
        submit: "Falha ao salvar. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Formata opções de categoria e fornecedor para campos de seleção
  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const supplierOptions = suppliers.map((sup) => ({
    value: sup.id,
    label: sup.name,
  }));

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      width="800px"
      maxWidth="95%"
      zIndex={1300}
    >
      <ModalHeader
        title={editMode ? "Editar Nota" : "Lançar Nova Nota"}
        icon={MoneyIcon}
        onClose={onClose}
      />
      <ModalContent>
        <Grid container spacing={2}>
          {!useMultipleCategories && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Categoria"
                required
                value={formData.category || ""}
                onChange={(e) => handleChange("category", e.target.value)}
                variant="outlined"
                error={!!errors.category}
                helperText={errors.category}
                disabled={loading}
              >
                <MenuItem value="" disabled>
                  Selecione uma categoria
                </MenuItem>
                {categoryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Seleção de Fornecedor */}
          <Grid item xs={12} sm={useMultipleCategories ? 12 : 6}>
            <TextField
              fullWidth
              select
              label="Fornecedor"
              value={formData.supplier || ""}
              onChange={(e) => handleChange("supplier", e.target.value)}
              variant="outlined"
              disabled={loading}
            >
              <MenuItem value="">Nenhum</MenuItem>
              {supplierOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Valor */}
          <Grid item xs={12} sm={6}>
            <MoneyTextField
              label="Valor (R$)"
              required
              value={formData.value || ""}
              setValue={(value) => handleChange("value", value)}
              error={!!errors.value}
              helperText={errors.value}
              fullWidth={true}
            />
          </Grid>

          {/* Data */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Data"
              value={formData.date}
              onChange={(date) => handleChange("date", date)}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  variant: "outlined",
                  error: !!errors.date,
                  helperText: errors.date,
                  disabled: loading,
                },
              }}
            />
          </Grid>

          {/* Número da Nota Fiscal */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Número da Nota Fiscal"
              value={formData.invoice_number || ""}
              onChange={(e) => handleChange("invoice_number", e.target.value)}
              variant="outlined"
              disabled={loading}
            />
          </Grid>

          {/* Observações */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={formData.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              variant="outlined"
              disabled={loading}
            />
          </Grid>

          {!editMode && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useMultipleCategories}
                      onChange={handleMultipleCategoriesToggle}
                      disabled={loading}
                    />
                  }
                  label="Alocar custo em múltiplas categorias"
                />
                <Tooltip title="Divide o custo total entre múltiplas categorias com base nas porcentagens ou valores especificados">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          )}

          {/* Modo de entrada para múltiplas categorias */}
          {useMultipleCategories && (
            <Grid item xs={12}>
              <Box mb={1}>
                <Tabs
                  value={inputMode}
                  onChange={handleInputModeChange}
                  variant="fullWidth"
                  sx={{ mb: 2 }}
                >
                  <Tab
                    value="amount"
                    label="Definir por Valor"
                    icon={<MoneyIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    value="percentage"
                    label="Definir por Porcentagem"
                    icon={<PercentIcon />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>
            </Grid>
          )}

          {/* Tabela de Múltiplas Categorias */}
          {useMultipleCategories && (
            <Grid item xs={12}>
              <Box mb={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Alocações por Categoria
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="40%">Categoria</TableCell>
                        {inputMode === "percentage" ? (
                          <>
                            <TableCell width="25%">Porcentagem (%)</TableCell>
                            <TableCell width="25%">Valor (R$)</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell width="25%">Valor (R$)</TableCell>
                            <TableCell width="25%">Porcentagem (%)</TableCell>
                          </>
                        )}
                        <TableCell width="10%" align="center">
                          Ações
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryAllocations.map((allocation) => (
                        <TableRow key={allocation.id}>
                          <TableCell>
                            <TextField
                              fullWidth
                              select
                              size="small"
                              value={allocation.categoryId || ""}
                              onChange={(e) =>
                                handleCategoryChange(
                                  allocation.id,
                                  e.target.value
                                )
                              }
                              variant="outlined"
                              error={!!errors.categories}
                              disabled={loading}
                            >
                              <MenuItem value="" disabled>
                                Selecione categoria
                              </MenuItem>
                              {categoryOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>

                          {inputMode === "percentage" ? (
                            <>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                  value={allocation.percentage || ""}
                                  onChange={(e) =>
                                    handlePercentageChange(
                                      allocation.id,
                                      e.target.value
                                    )
                                  }
                                  variant="outlined"
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        %
                                      </InputAdornment>
                                    ),
                                  }}
                                  disabled={loading}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography>
                                  R${" "}
                                  {calculateAmount(allocation.percentage || 0)}
                                </Typography>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>
                                <MoneyTextField
                                  fullWidth
                                  value={allocation.amount || ""}
                                  setValue={(value) =>
                                    handleAmountChange(allocation.id, value)
                                  }
                                  disabled={loading}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography>
                                  {allocation.percentage || 0}%
                                </Typography>
                              </TableCell>
                            </>
                          )}

                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleRemoveCategoryAllocation(allocation.id)
                              }
                              disabled={
                                categoryAllocations.length <= 1 || loading
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddCategoryAllocation}
                    disabled={
                      loading || categoryAllocations.length >= categories.length
                    }
                  >
                    Adicionar Categoria
                  </Button>

                  <Typography
                    variant="body2"
                    color={
                      totalPercentage === 100 ? "success.main" : "error.main"
                    }
                  >
                    Total: {totalPercentage}%{" "}
                    {errors.percentageTotal && `(${errors.percentageTotal})`}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Mensagem de erro */}
          {errors.submit && (
            <Grid item xs={12}>
              <Typography color="error">{errors.submit}</Typography>
            </Grid>
          )}
        </Grid>
      </ModalContent>
      <ModalFooter>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ mr: 1 }}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            loading ||
            (useMultipleCategories
              ? categoryAllocations.length === 0 ||
                totalPercentage !== 100 ||
                !formData.value ||
                !formData.date
              : !formData.category || !formData.value || !formData.date)
          }
        >
          {loading
            ? "Salvando..."
            : editMode
            ? "Atualizar"
            : useMultipleCategories
            ? "Criar Múltiplos"
            : "Criar"}
        </Button>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default CostModal;
